import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { X, Loader2, Clock, MapPin, User, CheckCircle2, Utensils, Home, Users as UsersIcon, Video, ShoppingBag, Lock } from "lucide-react";
import { GiftCodeField } from "@/components/GiftCodeField";
import api, { formatApiError } from "@/lib/api";
import { payFor } from "@/lib/checkout";
import PaymentChoice from "@/components/PaymentChoice";
import { gateAgreementFromError } from "@/lib/gate";
import AgreementSignModal from "@/components/AgreementSignModal";
import IntakeFormFill from "@/components/IntakeFormFill";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { isVirtualEvent } from "@/lib/events";
import { SocialShare } from "@/components/SocialShare";
import { toast } from "sonner";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";
import ListingDescription from "@/components/ListingDescription";

// Mirrors the server's host check; the server still enforces access.
const canHostStream = (user, ev) => !!user && !ev.is_placeholder && (
  ["admin", "staff"].includes(user.role) || [ev.owner_id, ev.facilitator_id, ev.enhancer_id].includes(user.id) || (ev.facilitator_ids || []).includes(user.id)
  || (ev.collaborators || []).some((c) => c.enhancer_id === user.id)
  || (!!user.manages_location_id && user.manages_location_id === ev.location_id && !["virtual", "all", ""].includes(ev.location_id || "")));

export default function EventModal({ event, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const { addTicket } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [eventForm, setEventForm] = useState(null);
  const [formDone, setFormDone] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const [pendingPay, setPendingPay] = useState(null);
  const [agreement, setAgreement] = useState(null);

  const tiers = event?.tiers || [];
  const today = new Date().toISOString().slice(0, 10);
  const openTiers = tiers.filter((t) => !t.closes_on || t.closes_on >= today);
  const allTiersClosed = tiers.length > 0 && openTiers.length === 0;
  const meals = event?.meal_options || [];
  const lodging = event?.lodging_options || [];
  const [tier, setTier] = useState(openTiers[0]?.name || "");
  const [meal, setMeal] = useState("");
  const [lodge, setLodge] = useState("");
  const [qty, setQty] = useState(1);
  const [giftCode, setGiftCode] = useState("");
  const [reservedInPerson, setReservedInPerson] = useState(null);

  const isMember = user && ["community", "enhancer", "staff", "council", "admin"].includes(user.role);
  const memberIncluded = event?.member_included && isMember;
  // Membership access gate (Phase 1): rank guest=0, discovery=1, community-and-up=2.
  const _rank = (r) => (!r ? 0 : r === "discovery" ? 1 : ["community", "enhancer", "staff", "council", "admin"].includes(r) ? 2 : 0);
  const _needRank = { public: 0, discovery: 1, all_members: 1, community: 2 };
  const accessLevel = event?.access_level || "public";
  const accessBlocked = _rank(user?.role) < (_needRank[accessLevel] || 0);
  const accessLabel = { discovery: "Discovery members", community: "Community members", all_members: "members" }[accessLevel] || "members";

  const total = useMemo(() => {
    if (!event) return 0;
    let base = tiers.length ? Number(tiers.find((t) => t.name === tier)?.price || 0) : Number(event.price || 0);
    if (memberIncluded) base = 0;
    const mp = Number(meals.find((m) => m.label === meal)?.price || 0);
    const lp = Number(lodging.find((l) => l.label === lodge)?.price || 0);
    return (base + mp + lp) * Math.max(1, qty);
  }, [event, tier, meal, lodge, tiers, meals, lodging, memberIncluded, qty]);

  if (!event) return null;

  const register = async (paymentMethod = "") => {
    if (!user) { toast.info("Please log in or join to register."); nav("/login"); return; }
    setError(""); setLoading(true);
    try {
      const { data } = await api.post(`/events/${event.id}/register`, { name: user.name || "", tier, meal_option: meal, lodging_option: lodge, quantity: Math.max(1, qty), payment_method: paymentMethod, gift_card_code: giftCode });
      const reg = data.registration;
      try {
        const { data: af } = await api.get(`/events/${event.id}/attached-form`);
        if (af?.form) setEventForm({ ...af.form, registration_id: reg.id });
      } catch { /* optional */ }
      if (data.waitlisted || reg.status === "waitlisted") {
        setWaitlisted(true); setDone(true);
        toast.success("You're on the waitlist — we'll email you if a spot opens.");
      } else if (reg.payment_status === "pending_payment") {
        setReservedInPerson({ price: reg.price ?? 0, quantity: reg.quantity || 1 }); setDone(true);
        toast.success("Reserved — pay in person at the event.");
      } else if (reg.payment_status === "free" || reg.payment_status === "paid" || reg.status === "confirmed") {
        setDone(true); toast.success("You're registered — see you there!");
      } else {
        setPendingPay({ id: reg.id, price: reg.price ?? 0 });
      }
    } catch (err) {
      const ag = await gateAgreementFromError(err);
      if (ag) { setAgreement(ag); }
      else { setError(formatApiError(err.response?.data?.detail?.message || err.response?.data?.detail) || err.message); }
    } finally { setLoading(false); }
  };

  const afterSign = async () => { setAgreement(null); await register(); };

  const sel = "w-full rounded-lg border border-[#E5E0D8] px-3 py-2.5 text-sm outline-none focus:border-[#144F75] bg-white";

  return (
    <div className="fixed inset-0 z-[130] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="event-modal">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 eyw-reveal max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} data-testid="event-close" className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        {pendingPay ? (
          <PaymentChoice item={{ title: event.title, price: pendingPay.price, deposit_pct: event.deposit_pct }}
            quoteRef={{ registration_id: pendingPay.id }}
            onDeposit={(code) => payFor("registration", pendingPay.id, false, code)}
            onFull={(code) => payFor("registration", pendingPay.id, true, code)} />
        ) : done ? (
          <div className="text-center py-8" data-testid="event-success">
            <CheckCircle2 size={48} className={`mx-auto mb-4 ${waitlisted ? "text-[#E0A458]" : "text-[#144F75]"}`} />
            <h3 className="font-serif text-2xl text-[#1A242B]">{waitlisted ? "You're on the waitlist." : reservedInPerson ? "Reserved." : "Registered."}</h3>
            <p className="text-[#4A5D6B] mt-2">{event.title} · {fmtDate(event.date)}</p>
            {waitlisted && <p className="text-sm text-[#4A5D6B] mt-2">We'll email you the moment a spot opens up.</p>}
            {reservedInPerson && (
              <div className="mt-3 rounded-xl bg-[#144F75]/[0.06] border border-[#144F75]/20 px-4 py-3" data-testid="event-reserved-in-person">
                <p className="text-sm font-semibold text-[#144F75]">Pay {money(Number(reservedInPerson.price))} in person</p>
                <p className="text-xs text-[#4A5D6B] mt-1">Your {reservedInPerson.quantity > 1 ? `${reservedInPerson.quantity} spots are` : "spot is"} held — please pay when you arrive at the event.</p>
              </div>
            )}
            {eventForm && !formDone && !waitlisted && (
              <div className="mt-6 text-left rounded-2xl border border-[#D98E73]/40 bg-[#D98E73]/[0.04] p-5" data-testid="event-form-prompt">
                <IntakeFormFill registrationId={eventForm.registration_id} formId={eventForm.form_id}
                  questions={eventForm.questions} title={eventForm.title}
                  onDone={() => setFormDone(true)} onSkip={() => setFormDone(true)} />
              </div>
            )}
            {formDone && <p className="text-sm text-[#5C8A72] mt-4" data-testid="event-form-done">Thanks — your answers are with the organizer.</p>}
            <button onClick={onClose} className="eyw-btn mt-6">Done</button>
          </div>
        ) : (
          <>
            <p className="eyw-label mb-1">{event.category}</p>
            <h3 className="font-serif text-2xl text-[#1A242B]">{event.title}</h3>
            <div className="mt-2"><SocialShare path={`/events/${event.slug || event.id}`} ogPath={`/api/share/event/${event.slug || event.id}`} title={event.title} testid="event-share" label="Share this event" /></div>
            {isVirtualEvent(event) && (
              <span data-testid="event-online-badge" className="inline-flex items-center gap-1.5 mt-2 rounded-full bg-[#144F75]/10 text-[#144F75] text-xs font-semibold px-3 py-1">
                <Video size={13} /> Online / virtual
              </span>
            )}
            {event.has_stream && (
              <Link to={`/events/${event.slug || event.id}/watch`} onClick={onClose} data-testid="event-watch-link"
                className="ml-2 inline-flex items-center gap-1.5 mt-2 rounded-full bg-red-600/10 text-red-700 text-xs font-semibold px-3 py-1 hover:bg-red-600/20 transition-colors">
                <Video size={13} /> {event.stream_status === "ended" ? "Watch the replay" : "Watch page"}
              </Link>
            )}
            {canHostStream(user, event) && (
              <Link to={`/events/${event.slug || event.id}/studio`} onClick={onClose} data-testid="event-host-stream-link"
                className="ml-2 inline-flex items-center gap-1.5 mt-2 rounded-full bg-[#144F75]/10 text-[#144F75] text-xs font-semibold px-3 py-1 hover:bg-[#144F75]/20 transition-colors">
                <Video size={13} /> Set up live stream
              </Link>
            )}
            <SeriesDates event={event} />
            <div className="space-y-1.5 text-sm text-[#4A5D6B] mt-3">
              {Array.isArray(event.days) && event.days.length > 0 ? (
                <div data-testid="event-multiday-schedule" className="rounded-xl border border-[#E5E0D8] bg-white px-3 py-2.5">
                  <div className="text-xs font-semibold text-[#9E5C7F] uppercase tracking-wide mb-1.5">Multi-day schedule</div>
                  {event.days.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-[13px] text-[#1A242B] py-0.5">
                      <Clock size={13} className="text-[#9E5C7F]" />
                      <span className="font-medium">{fmtDate(d.date)}</span>
                      <span className="text-[#4A5D6B]">{fmtTimeRange(d.start_time, d.end_time)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2"><Clock size={14} /> {fmtDate(event.date)} · {fmtTimeRange(event.start_time, event.end_time)}</div>
              )}
              <div className="flex items-center gap-2"><MapPin size={14} /> {event.location}</div>
              {event.facilitator && <div className="flex items-center gap-2"><User size={14} /> {event.facilitator}</div>}
            </div>
            <ListingDescription text={event.description} item={event} kind="event" html className="article-html text-sm text-[#4A5D6B] mt-4 leading-relaxed" testid="event-description" />

            {tiers.length > 0 && (
              <div className="mt-5">
                <label className="text-sm font-medium text-[#1A242B] flex items-center gap-1.5 mb-1.5"><UsersIcon size={14} /> Ticket</label>
                {allTiersClosed ? (
                  <p className="text-sm text-[#B5794E] rounded-xl border border-[#E0A458]/40 bg-[#E0A458]/10 px-4 py-3" data-testid="event-tiers-closed">Ticket sales for this event have ended.</p>
                ) : (
                  <>
                    <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="event-tier-select" className={sel}>
                      {openTiers.map((t) => <option key={t.name} value={t.name}>{t.name} — {money(Number(t.price))}</option>)}
                    </select>
                    {tiers.find((t) => t.name === tier)?.description && (
                      <p className="text-xs text-[#8FA89B] mt-1" data-testid="event-tier-description">{tiers.find((t) => t.name === tier).description}</p>
                    )}
                    {tiers.find((t) => t.name === tier)?.closes_on && (
                      <p className="text-xs text-[#B5794E] mt-1" data-testid="event-tier-deadline">Price available through {tiers.find((t) => t.name === tier).closes_on}</p>
                    )}
                  </>
                )}
              </div>
            )}
            {meals.length > 0 && (
              <div className="mt-4">
                <label className="text-sm font-medium text-[#1A242B] flex items-center gap-1.5 mb-1.5"><Utensils size={14} /> Meal (optional)</label>
                <select value={meal} onChange={(e) => setMeal(e.target.value)} data-testid="event-meal-select" className={sel}>
                  <option value="">No meal</option>
                  {meals.map((m) => <option key={m.label} value={m.label}>{m.label} — +{money(Number(m.price))}</option>)}
                </select>
              </div>
            )}
            {lodging.length > 0 && (
              <div className="mt-4">
                <label className="text-sm font-medium text-[#1A242B] flex items-center gap-1.5 mb-1.5"><Home size={14} /> Lodging (optional)</label>
                <select value={lodge} onChange={(e) => setLodge(e.target.value)} data-testid="event-lodging-select" className={sel}>
                  <option value="">No lodging</option>
                  {lodging.map((l) => <option key={l.label} value={l.label}>{l.label} — +{money(Number(l.price))}</option>)}
                </select>
              </div>
            )}

            {!memberIncluded && total > 0 && (
              <div className="mt-5 flex items-center justify-between rounded-xl border border-[#E5E0D8] bg-white px-4 py-2.5" data-testid="event-qty-row">
                <label className="text-sm font-medium text-[#1A242B] flex items-center gap-1.5"><UsersIcon size={14} /> Tickets</label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="event-qty-dec" className="h-7 w-7 grid place-items-center rounded-full border border-[#E5E0D8] text-[#144F75] hover:bg-[#144F75]/5 text-lg leading-none">−</button>
                  <span data-testid="event-qty" className="w-6 text-center font-semibold text-[#1A242B]">{qty}</span>
                  <button type="button" onClick={() => setQty((q) => q + 1)} data-testid="event-qty-inc" className="h-7 w-7 grid place-items-center rounded-full border border-[#E5E0D8] text-[#144F75] hover:bg-[#144F75]/5 text-lg leading-none">+</button>
                </div>
              </div>
            )}
            <div className="mt-4 text-2xl font-serif text-[#144F75]" data-testid="event-total">
              {total <= 0 ? (memberIncluded ? "Included with membership" : "Free") : `${money(total)}`}
              {total > 0 && qty > 1 && <span className="text-sm text-[#4A5D6B] font-sans ml-2">for {qty} tickets</span>}
            </div>
            {error && <p className="text-sm text-red-600 mt-3" data-testid="event-error">{error}</p>}
            {event.cancelled ? (
              <div className="w-full mt-5 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-center" data-testid="event-cancelled-notice">
                <p className="text-sm font-semibold text-red-600">This session has been canceled.</p>
                <p className="text-xs text-[#4A5D6B] mt-1">Questions? Call 859-979-5505 or email info@enhancingyourworld.com.</p>
              </div>
            ) : accessBlocked ? (
              <div className="mt-5 rounded-xl bg-[#144F75]/[0.06] border border-[#144F75]/20 px-4 py-4 text-center" data-testid="event-access-gate">
                <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#144F75]"><Lock size={14} /> Member Access Required</p>
                <p className="text-xs text-[#4A5D6B] mt-1">This event is open to {accessLabel} only.</p>
                <button onClick={() => { onClose(); nav(user ? "/memberships" : "/join"); }} data-testid="event-access-join" className="eyw-btn !py-2 text-sm mt-3">
                  {user ? "Upgrade membership" : "Join to book"}
                </button>
              </div>
            ) : allTiersClosed ? null : (
              <>
                {event.is_full ? (
                  <p data-testid="event-full-note" className="mt-5 text-sm rounded-xl bg-[#B5794E]/10 text-[#B5794E] px-4 py-3">{event.waitlist_enabled === false ? "This session is fully booked." : "This session is full — join the waitlist and we'll email you the moment a spot opens."}</p>
                ) : event.spots_left != null && event.spots_left <= 5 ? (
                  <p data-testid="event-spots-left" className="mt-5 text-sm text-[#B5794E] font-medium">Only {event.spots_left} spot{event.spots_left === 1 ? "" : "s"} left</p>
                ) : null}
                {!event.is_full && total > 0 && <div className="mt-4"><GiftCodeField value={giftCode} onChange={setGiftCode} due={total} testid="event-gift-code" /></div>}
                {!(event.is_full && event.waitlist_enabled === false) && (
                <button onClick={() => register("")} disabled={loading} data-testid="event-register" className="eyw-btn w-full mt-5 disabled:opacity-60">
                  {loading && <Loader2 className="animate-spin" size={18} />}
                  {event.is_full ? "Join the waitlist" : total <= 0 ? "Register" : "Register & Pay"}
                </button>
                )}
                {total > 0 && !event.is_full && (
                  <button onClick={() => register("in_person")} disabled={loading} data-testid="event-reserve-in-person" className="eyw-btn-outline w-full mt-3 inline-flex items-center justify-center gap-2">
                    <MapPin size={16} /> Reserve &amp; pay in person
                  </button>
                )}
                {total > 0 && (
                  <button
                    onClick={() => {
                      addTicket(event, { tier, price: total, meal_option: meal, lodging_option: lodge });
                      toast.success("Ticket added to your cart");
                      onClose();
                    }}
                    data-testid="event-add-to-cart"
                    className="eyw-btn-outline w-full mt-3 inline-flex items-center justify-center gap-2"
                  >
                    <ShoppingBag size={16} /> Add ticket to cart
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
      {agreement && <AgreementSignModal agreement={agreement} onClose={() => setAgreement(null)} onSigned={afterSign} />}
    </div>
  );
}

// Other upcoming dates in a repeating series (from the series_dates list kept on each occurrence).
function SeriesDates({ event }) {
  const today = new Date().toISOString().slice(0, 10);
  const others = (event.series_dates || []).filter((d) => d.id !== event.id && !d.cancelled && d.date >= today).slice(0, 6);
  if (!others.length) return null;
  return (
    <div className="mt-3 text-xs text-[#4A5D6B]" data-testid="event-series-dates">
      <span className="font-semibold text-[#144F75]">Also on: </span>
      {others.map((d, i) => (
        <span key={d.id}>{i > 0 && ", "}<Link to={`/events/${d.id}`} className="underline hover:text-[#144F75]" data-testid={`event-series-date-${d.id}`}>{fmtDate(d.date)}</Link></span>
      ))}
    </div>
  );
}
